import React, { FC, useMemo } from 'react';
import { getObjectValues } from '../../utils/DataAccessUtils';
import { Popover } from 'antd';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { MAX_OW_RADIO_TEXT_LENGTH } from '../../utils/DataConstants';

require('./VisualCheckboxInput.less');

const OWCheckboxDisplayWithIcons = ({
	sortedVisibleRecords,
	valueAccessor,
	fieldName,
	value,
	searchText,
	handleSameValueClicked,
	handleValueChange,
	renderRecord,
	dropdownRecords,
	canShowList = false,
	getDescription,
}) => {
	return (
		<div className="radio-container radio-container--center state-in-box">
			{sortedVisibleRecords.map((option) => {
				const renderedRecord = renderRecord(option, dropdownRecords, searchText);
				const isSelected = Array.isArray(value)
					? value.includes(valueAccessor(option))
					: value === valueAccessor(option);
				return (
					<Popover content={getDescription ? getDescription(option, searchText) : null}>
						<div key={valueAccessor(option)} className={option.disabled ? `disabled` : ''}>
							<div
								className={`radio-outer ${isSelected ? 'checked' : ''} ${
									option.disabled ? 'disabled' : ''
								}`}
							>
								<input
									key={valueAccessor(option)}
									type="radio"
									name={fieldName}
									id={valueAccessor(option)}
									required={option.required}
									disabled={option.disabled}
									onClick={handleSameValueClicked}
									checked={isSelected}
									onChange={handleValueChange}
									value={valueAccessor(option)}
								/>
								<label htmlFor={valueAccessor(option)}>
									<span data-pop-class="radio-pop abc">
										<div>
											<div className="state-in-box-inner">
												<div className="state-map">
													<i className={`icons8-font ${option.icon}`} />
												</div>
												{typeof renderedRecord === 'string' && canShowList ? (
													<Ellipsis tooltip={true} length={MAX_OW_RADIO_TEXT_LENGTH}>
														{renderedRecord}
													</Ellipsis>
												) : (
													renderedRecord
												)}
											</div>
										</div>
									</span>
								</label>
								<br />
								<div className="radio-box">
									<div className={`checkbox-square`}></div>
								</div>
							</div>
							<span className="feature-list"></span>
						</div>
					</Popover>
				);
			})}
		</div>
	);
};

const OWCheckboxDisplayWithoutIcon = ({
	sortedVisibleRecords,
	valueAccessor,
	fieldName,
	value,
	searchText,
	handleSameValueClicked,
	handleValueChange,
	renderRecord,
	dropdownRecords,
	canShowList = false,
	getDescription,
}) => {
	return (
		<div className="radio-container radio-container--center">
			{sortedVisibleRecords.map((option) => {
				const renderedRecord = renderRecord(option, dropdownRecords, searchText);
				const isSelected = Array.isArray(value)
					? value.includes(valueAccessor(option))
					: value === valueAccessor(option);
				return (
					<Popover content={getDescription ? getDescription(option, searchText) : null}>
						<div key={valueAccessor(option)} className={option.disabled ? `disabled` : ''}>
							<div
								className={`radio-outer ${isSelected ? 'checked' : ''} ${
									option.disabled ? 'disabled' : ''
								}`}
							>
								<input
									key={valueAccessor(option)}
									type={'checkbox'}
									name={fieldName}
									id={valueAccessor(option)}
									required={option.required}
									disabled={option.disabled}
									onClick={handleSameValueClicked}
									checked={isSelected}
									onChange={handleValueChange}
									value={valueAccessor(option)}
								/>
								<label htmlFor={valueAccessor(option)}>
									<span data-pop-class="radio-pop">
										{typeof renderedRecord === 'string' && canShowList ? (
											<Ellipsis tooltip={true} length={MAX_OW_RADIO_TEXT_LENGTH}>
												{renderedRecord}
											</Ellipsis>
										) : (
											renderedRecord
										)}
									</span>
								</label>
								<br />
								<div className="radio-box">
									<div className={`checkbox-square`}></div>
								</div>
							</div>
							<span className="feature-list"></span>
						</div>
					</Popover>
				);
			})}
		</div>
	);
};

const OWMultiSelect: FC<any> = ({
	valueAccessor = (el) => el.id,
	records,
	outerStyle = {},
	optionStyle = {},
	size = 'default',
	style = {},
	fieldName,
	value = [],
	onChange,
	loading,
	disabled,
	emptyWhenDisabled,
	sortBy,
	sorter = (records) => records.sort((a, b) => (a.name > b.name ? 1 : -1)),
	renderRecord,
	getDescription,
	canShowList,
}): React.ReactElement => {
	const handleValueChange = (e) => {
		const selectedValue = e.target.value;
		const newValue = !value
			? [value]
			: value.includes(selectedValue)
			? value.filter((_) => _ !== selectedValue)
			: [...value, selectedValue];
		onChange(newValue);
	};

	const dropdownRecords = useMemo(() => getObjectValues(records), [records]);
	const sortedVisibleRecords = useMemo(() => sorter(dropdownRecords), [dropdownRecords, sorter]);

	return (
		<div className={canShowList ? 'radio-container-list-view' : ''}>
			{sortedVisibleRecords.some((option) => option.icon) ? (
				<OWCheckboxDisplayWithIcons
					getDescription={getDescription}
					sortedVisibleRecords={sortedVisibleRecords}
					valueAccessor={valueAccessor}
					fieldName={fieldName}
					value={value}
					searchText={''}
					handleSameValueClicked={handleValueChange}
					handleValueChange={handleValueChange}
					renderRecord={renderRecord}
					dropdownRecords={dropdownRecords}
					canShowList={canShowList}
				/>
			) : (
				<OWCheckboxDisplayWithoutIcon
					getDescription={getDescription}
					sortedVisibleRecords={sortedVisibleRecords}
					valueAccessor={valueAccessor}
					fieldName={fieldName}
					value={value}
					searchText={''}
					handleSameValueClicked={handleValueChange}
					handleValueChange={handleValueChange}
					renderRecord={renderRecord}
					dropdownRecords={dropdownRecords}
					canShowList={canShowList}
				/>
			)}
		</div>
	);
};

export default OWMultiSelect;
